import React from 'react';
import { MessageBarBody } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';

import { ContractReview, ContractReviewFinding, FindingDecision } from '../../../../models/Entities/Contracts/ContractReview';
import { GuideMessageBar } from '../../../../theme/components/GuideMessageBar';
import { GuideSectionTitle } from '../../../../theme/components/GuideSectionTitle';
import { splitNewAddendumReview } from '../ContractReview.Functions';
import { formatValidity } from '../ContractReviewApply/ContractReviewApplyDialog.Component';
import { ContractReviewItemCard } from '../ContractReviewItemCard/ContractReviewItemCard.Component';

// Cabecera del resultado NewAddendum (US9): la adenda propuesta (nombre, descripción, vigencia) con su alta decidible,
// y el ajuste del fin de vigencia de la adenda base si vino. El resto de los cambios va en la lista normal.

export interface ContractReviewNewAddendumHeaderProps {
  review: ContractReview;
  baseAddendumName?: string;
  deciding?: number[];
  onDecide?: (finding: ContractReviewFinding, decision: FindingDecision) => void;
}

export const ContractReviewNewAddendumHeader: React.FC<ContractReviewNewAddendumHeaderProps> = ({ review, baseAddendumName, deciding, onDecide }) => {
  const { t } = useTranslation();
  const split = splitNewAddendumReview(review);

  const card = (finding: ContractReviewFinding) => (
    <ContractReviewItemCard review={review} finding={finding} deciding={deciding?.includes(finding.id)} onDecide={onDecide ? decision => onDecide(finding, decision) : undefined} />
  );

  return (
    <section className="crv-new-addendum" aria-labelledby="new-addendum-title" data-testid="new-addendum-header">
      <GuideSectionTitle
        id="new-addendum-title"
        title={t('contractReview.newAddendum.title')}
        description={baseAddendumName ? t('contractReview.newAddendum.changesFrom', { base: baseAddendumName }) : undefined}
      />
      {!split.finding || !split.fields ? (
        <GuideMessageBar intent="warning" layout="multiline">
          <MessageBarBody>{t('contractReview.newAddendum.noCreate')}</MessageBarBody>
        </GuideMessageBar>
      ) : (
        <>
          <dl className="crv-details crv-details--inline">
            <dt>{t('contractReview.newAddendum.name')}</dt>
            <dd>{split.fields.ContractAddendumName}</dd>
            {split.fields.ContractAddendumDescription && (
              <>
                <dt>{t('contractReview.newAddendum.description')}</dt>
                <dd>{split.fields.ContractAddendumDescription}</dd>
              </>
            )}
            <dt>{t('contractReview.newAddendum.validity')}</dt>
            <dd>{formatValidity(t, split.fields.ContractAddendumExpiryStartDate, split.fields.ContractAddendumExpiryEndDate)}</dd>
          </dl>
          {card(split.finding)}
        </>
      )}
      {split.baseValidityFinding && (
        <>
          <h4 className="crv-subtitle">{t('contractReview.newAddendum.baseAdjustment')}</h4>
          {card(split.baseValidityFinding)}
        </>
      )}
    </section>
  );
};

export default ContractReviewNewAddendumHeader;
